import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Text,
} from '@chakra-ui/react';
import { AiOutlineCheck, AiOutlineClose, AiOutlineDown } from 'react-icons/ai';

// Remove acentos e baixa a caixa para comparar busca x opção
const normalizar = (txt) =>
    String(txt == null ? '' : txt)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');

/**
 * SearchableSelect — dropdown pesquisável padrão do CRM (substitui o <Select> nativo).
 *
 * Ao focar, abre a lista completa; ao digitar, filtra as opções (ignora acentos).
 * A seleção usa onMouseDown (dispara antes do onBlur), no mesmo padrão dos
 * autocompletes de Empresa/Contato/Responsável já existentes no projeto.
 *
 * Props:
 *   value        — valor selecionado (comparado com String(option.value))
 *   onChange     — (valor, opcao) => void; recebe null ao limpar
 *   options      — array de { value, label, description? } ou de strings
 *   placeholder  — texto quando nada está selecionado (vira a opção "limpar" se isClearable)
 *   isClearable  — mostra a opção de limpar (equivale ao placeholder do Select nativo)
 *   size         — 'sm' | 'md' (padrão 'md')
 *   leftIcon     — ícone react-icons/ai opcional à esquerda
 *   menuPlacement — 'bottom' | 'top' (padrão 'bottom')
 *   flex/maxW/minW/w/width/alignSelf — layout (aplicados no wrapper)
 *   ...resto     — estilos repassados ao Input (bg, borderColor, _focus, fontSize...)
 */
const SearchableSelect = ({
    value,
    onChange,
    options = [],
    placeholder = 'Selecione...',
    noResultsText = 'Nenhuma opção encontrada',
    isClearable = false,
    size = 'md',
    leftIcon = null,
    menuPlacement = 'bottom',
    flex,
    maxW,
    minW,
    w,
    width,
    alignSelf,
    ...inputProps
}) => {
    const [aberto, setAberto] = useState(false);
    const [busca, setBusca] = useState('');
    const [destaque, setDestaque] = useState(-1);
    const inputRef = useRef(null);

    const opcoes = useMemo(
        () => (options || []).map(o => (o && typeof o === 'object' ? o : { value: o, label: String(o) })),
        [options]
    );

    const selecionada = useMemo(() => {
        if (value === null || value === undefined || value === '') return null;
        return opcoes.find(o => String(o.value) === String(value)) || null;
    }, [opcoes, value]);

    const filtradas = useMemo(() => {
        const q = normalizar(busca).trim();
        if (!q) return opcoes;
        return opcoes.filter(o =>
            normalizar(o.label).includes(q) || (o.description && normalizar(o.description).includes(q))
        );
    }, [opcoes, busca]);

    const abrir = () => {
        setBusca('');
        setDestaque(-1);
        setAberto(true);
    };

    const fechar = () => {
        setAberto(false);
        setBusca('');
        setDestaque(-1);
    };

    const selecionar = (opt) => {
        if (onChange) onChange(opt ? opt.value : null, opt || null);
        fechar();
        if (inputRef.current) inputRef.current.blur();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!aberto) return abrir();
            setDestaque(d => Math.min(d + 1, filtradas.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setDestaque(d => Math.max(d - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (!aberto) return abrir();
            const opt = filtradas[destaque >= 0 ? destaque : 0];
            if (opt) selecionar(opt);
        } else if (e.key === 'Escape') {
            fechar();
            if (inputRef.current) inputRef.current.blur();
        }
    };

    const mostrarLimpar = isClearable && selecionada && !aberto;

    return (
        <Box position="relative" w={w || width || '100%'} flex={flex} maxW={maxW} minW={minW} alignSelf={alignSelf}>
            <InputGroup size={size}>
                {leftIcon && (
                    <InputLeftElement pointerEvents="none">
                        <Icon as={leftIcon} color="textMuted" boxSize="14px" />
                    </InputLeftElement>
                )}
                <Input
                    ref={inputRef}
                    value={aberto ? busca : (selecionada ? selecionada.label : '')}
                    onChange={e => {
                        setBusca(e.target.value);
                        setDestaque(0);
                        if (!aberto) setAberto(true);
                    }}
                    onFocus={abrir}
                    onBlur={() => setTimeout(fechar, 150)}
                    onKeyDown={handleKeyDown}
                    placeholder={aberto && selecionada ? selecionada.label : placeholder}
                    autoComplete="off"
                    {...inputProps}
                />
                <InputRightElement
                    cursor="pointer"
                    onMouseDown={e => {
                        e.preventDefault();
                        if (mostrarLimpar) return selecionar(null);
                        if (aberto) {
                            fechar();
                            if (inputRef.current) inputRef.current.blur();
                        } else if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }}
                >
                    <Icon
                        as={mostrarLimpar ? AiOutlineClose : AiOutlineDown}
                        color="textMuted"
                        boxSize="12px"
                        transform={aberto ? 'rotate(180deg)' : 'none'}
                        transition="transform 0.15s ease"
                    />
                </InputRightElement>
            </InputGroup>

            {aberto && (
                <Box
                    position="absolute"
                    top={menuPlacement === 'top' ? undefined : 'calc(100% + 4px)'}
                    bottom={menuPlacement === 'top' ? 'calc(100% + 4px)' : undefined}
                    left={0}
                    right={0}
                    zIndex={10000}
                    bg="surface"
                    border="1px solid"
                    borderColor="borderPrimary"
                    borderRadius="xl"
                    overflow="hidden"
                    overflowY="auto"
                    maxH="260px"
                    boxShadow="0 12px 40px rgba(0,0,0,0.45)"
                >
                    {isClearable && (
                        <Box
                            px={3}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: 'surfaceHover' }}
                            onMouseDown={() => selecionar(null)}
                            borderBottom="1px solid"
                            borderColor="borderSubtle"
                        >
                            <Text fontSize="sm" color="textMuted" fontStyle="italic">
                                {placeholder}
                            </Text>
                        </Box>
                    )}

                    {filtradas.length === 0 ? (
                        <Box px={3} py={3} textAlign="center">
                            <Text fontSize="xs" color="textMuted">{noResultsText}</Text>
                        </Box>
                    ) : (
                        filtradas.map((o, i) => {
                            const ativa = selecionada && String(o.value) === String(selecionada.value);
                            return (
                                <Box
                                    key={`${o.value}-${i}`}
                                    px={3}
                                    py={2}
                                    cursor="pointer"
                                    bg={i === destaque ? 'surfaceHover' : 'transparent'}
                                    _hover={{ bg: 'surfaceHover' }}
                                    onMouseDown={() => selecionar(o)}
                                    onMouseEnter={() => setDestaque(i)}
                                >
                                    <HStack spacing={2}>
                                        <Box flex={1} minW={0}>
                                            <Text
                                                fontSize="sm"
                                                color={ativa ? 'secondary' : 'textPrimary'}
                                                fontWeight={ativa ? '800' : '600'}
                                                isTruncated
                                            >
                                                {o.label}
                                            </Text>
                                            {o.description && (
                                                <Text fontSize="xs" color="textMuted" isTruncated>
                                                    {o.description}
                                                </Text>
                                            )}
                                        </Box>
                                        {ativa && <Icon as={AiOutlineCheck} color="secondary" boxSize="12px" flexShrink={0} />}
                                    </HStack>
                                </Box>
                            );
                        })
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SearchableSelect;
